import { Inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { OrderType } from '../api/contracts/order';
import { ApiService } from '../api/api.service';
import { StorageKeys, StorageService } from './storage.service';
import { Cart, CartItem } from '../types/cart';
import { Product } from '../api/contracts/product';
import { WeekDay } from '../api/contracts/menu';
import { APP_CONFIG, AppConfig } from '../config/app-config';
import { ModalService } from './modal.service';
import { WeekIndex } from '../types/menu';
import moment from 'moment';
import { getCurrentWeekNumber, getNextWeekNumber } from '../utils/date.utils';
import { NonNullableFormBuilder } from '@angular/forms';
import { MealMenuService } from './meal-menu.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly _currentCart = new BehaviorSubject<Cart>(
    this.createEmptyCart()
  );

  public readonly currentCart$: Observable<Cart>;
  public readonly totalPrice$: Observable<number>;
  public readonly shippingPrice$: Observable<number>;

  constructor(
    private readonly apiService: ApiService,
    private readonly storageService: StorageService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly modalService: ModalService,
    private readonly menuService: MealMenuService
  ) {
    const mainCurrency = this.appConfig.mainCurrency;
    this.currentCart$ = this._currentCart.asObservable();
    this.totalPrice$ = this._currentCart.pipe(
      map((cart) =>
        cart.items.reduce((sum, item) => {
          sum +=
            this.menuService.getRelevantProductPrice(item.product)[mainCurrency] *
            item.quantity;
          return sum;
        }, 0)
      )
    );

    this.shippingPrice$ = of(appConfig.shippingPrice).pipe(shareReplay(1));
  }

  init(): void {
    this.storageService
      .get<Cart | null>(StorageKeys.CART)
      .pipe(take(1))
      .subscribe((cart) => {
        if (cart) {
          this.writeToCart(cart).subscribe();
        } else {
          this.writeToCart(this.createEmptyCart()).subscribe();
        }
      });
  }

  switchOrderType(type: OrderType): Observable<Cart> {
    const cart = this.createEmptyCart();
    cart.orderType = type;
    return this.writeToCart(cart);
  }

  addItemToCart(
    item: Product,
    desiredOrderType: OrderType,
    day: WeekDay | null = null,
    weeklyMenuId: number | null = null,
    weekNumber: number | null = null
  ): void {
    const currentCart = this._currentCart.getValue();
    of(desiredOrderType !== currentCart.orderType)
      .pipe(
        switchMap((isOrderTypeChanging) => {
          if (isOrderTypeChanging && currentCart.items.length === 0) {
            return this.switchOrderType(desiredOrderType).pipe(map(() => true));
          }
          if (isOrderTypeChanging && currentCart.items.length > 0) {
            return this.modalService
              .showOrderTypeSwitchWarningPopup(
                currentCart.items.length,
                desiredOrderType
              )
              .pipe(
                switchMap((isConfirmedByUser) => {
                  if (!isConfirmedByUser) {
                    return of(false);
                  }
                  return this.switchOrderType(desiredOrderType).pipe(
                    map(() => true)
                  );
                })
              );
          }
          return of(true);
        }),
        switchMap((shouldAddToCart) => {
          if (shouldAddToCart === true) {
            const cart = Object.assign({}, this._currentCart.getValue());
            const existingProduct = cart.items.find(
              (p) =>
                p.product.id === item.id &&
                p.day === day &&
                p.weeklyMenuId === weeklyMenuId &&
                p.weekNumber === weekNumber
            );
            if (!existingProduct) {
              cart.items.push({
                product: item,
                quantity: 1,
                day: day,
                weeklyMenuId,
                weekNumber: weekNumber,
              });
            } else {
              this.increaseAmount(existingProduct);
            }
            return this.writeToCart(cart);
          }
          return EMPTY;
        })
      )
      .subscribe();
  }

  removeItemFromCart(cartItem: CartItem): void {
    const cart = Object.assign({}, this._currentCart.getValue());
    cart.items = cart.items.filter(
      this.getFilterCartItemPredicateFunc(cart, cartItem)
    );
    this.writeToCart(cart).subscribe();
  }

  increaseAmount(cartItem: CartItem): void {
    const cart = Object.assign({}, this._currentCart.getValue());
    const item = cart.items.find(
      this.getFindCartItemPredicateFunc(cart, cartItem)
    );
    if (!item) {
      return;
    }
    item.quantity++;
    this.writeToCart(cart).subscribe();
  }

  decreaseAmount(cartItem: CartItem): void {
    const cart = Object.assign({}, this._currentCart.getValue());
    const item = cart.items.find(
      this.getFindCartItemPredicateFunc(cart, cartItem)
    );
    if (!item) {
      return;
    }
    item.quantity--;
    if (item.quantity === 0) {
      this.removeItemFromCart(cartItem);
      return;
    }
    this.writeToCart(cart).subscribe();
  }

  clearCart(): void {
    const cart = this.createEmptyCart();
    this.writeToCart(cart).subscribe();
  }

  getDeliveryDaysOfWeek$(weekNumber: number): Observable<Set<WeekDay> | null> {
    return this._currentCart.pipe(
      map((cart) => {
        if (cart.orderType === OrderType.DELIVERY) {
          return new Set<WeekDay>(
            cart.items
              .filter((item) => item.weekNumber === weekNumber)
              .map((item) => item.day as WeekDay)
          );
        }
        return null;
      })
    );
  }

  getWeekSlotAsString(weekNumber: number): string {
    const daysInWeek = 7;
    const start = moment().week(weekNumber).startOf('week');
    const end = start.clone().add(daysInWeek - 1, 'days');
    return `${start.format('DD.MM')} — ${end.format('DD.MM')}`;
  }

  removeNotAvailableItemsItems(): Observable<Cart> {
    const cart = Object.assign({}, this._currentCart.getValue());
    const availableWeekNumbers = [getCurrentWeekNumber(), getNextWeekNumber()];
    cart.items = cart.items.filter((item) =>
      item.weekNumber ? availableWeekNumbers.includes(item.weekNumber) : true
    );
    return this.writeToCart(cart);
  }

  private createEmptyCart(): Cart {
    return {
      orderType: OrderType.DELIVERY,
      items: [],
    };
  }

  private writeToCart(cart: Cart): Observable<Cart> {
    this._currentCart.next(cart);
    return this.storageService.set<Cart>(StorageKeys.CART, cart);
  }

  private getFindCartItemPredicateFunc(
    cart: Cart,
    cartItemToCompare: CartItem
  ): (cartItem: CartItem) => boolean {
    return cart.orderType === OrderType.IN_STORE
      ? (item: CartItem) => item.product.id === cartItemToCompare.product.id
      : (item: CartItem) => {
          return (
            item.product.id === cartItemToCompare.product.id &&
            item.day === cartItemToCompare.day &&
            item.weekNumber === cartItemToCompare.weekNumber &&
            item.weeklyMenuId === cartItemToCompare.weeklyMenuId
          );
        };
  }

  private getFilterCartItemPredicateFunc(
    cart: Cart,
    cartItemToCompare: CartItem
  ): (cartItem: CartItem) => boolean {
    return cart.orderType === OrderType.IN_STORE
      ? (item: CartItem) => {
          return item.product.id !== cartItemToCompare.product.id;
        }
      : (item: CartItem) => {
          return !(
            item.product.id === cartItemToCompare.product.id &&
            item.day === cartItemToCompare.day &&
            item.weekNumber === cartItemToCompare.weekNumber &&
            item.weeklyMenuId === cartItemToCompare.weeklyMenuId
          );
        };
  }
}
